import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import apiClient, { formatApiError } from "@/api/client";
import { toast } from "sonner";
import CustomerSelect from "@/components/CustomerSelect";
import {
  Zap, ChevronLeft, Loader2, Globe, ListOrdered, Link2, Share2,
  Copy, CheckCircle2, AlertTriangle, RefreshCw, Search, Sparkles,
  FileDown, Trash2, ExternalLink, Key, ArrowRight, ShieldCheck,
} from "lucide-react";

const TABS = [
  { id: "indexnow", label: "Toplu İndex (IndexNow)", icon: Zap },
  { id: "gsc", label: "Search Console", icon: ShieldCheck },
  { id: "internal", label: "İç Bağlantı Önerisi", icon: Link2 },
  { id: "social", label: "Sosyal Patlama", icon: Share2 },
  { id: "history", label: "Geçmiş", icon: ListOrdered },
];

export default function IndexBoostPage() {
  const [tab, setTab] = useState("indexnow");
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    apiClient.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
  }, []);

  return (
    <div className="space-y-5" data-testid="indexboost-page">
      <div className="flex items-center gap-2 text-xs text-[#64748B]">
        <Link to="/ai-araclar" className="hover:text-[#4F46E5] flex items-center gap-1">
          <ChevronLeft size={14} /> AI Araçlar
        </Link>
        <span>/</span>
        <span className="font-medium text-[#0F172A]">IndexBoost</span>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border border-[#E0F2FE] bg-gradient-to-br from-[#F0F9FF] via-white to-[#EEF2FF] p-6">
        <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-[#0EA5E9]/10 blur-3xl" />
        <div className="absolute -left-10 -bottom-12 w-56 h-56 rounded-full bg-[#7C3AED]/10 blur-3xl" />
        <div className="relative flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0EA5E9] to-[#7C3AED] flex items-center justify-center shrink-0 shadow-lg shadow-sky-200">
            <Zap size={22} className="text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-[#0F172A]">IndexBoost — SEO Hızlandırıcı</h1>
            <p className="text-sm text-[#64748B] mt-1 max-w-3xl leading-relaxed">
              Yeni içeriklerinizi arama motorlarına anında tanıtın. <b>IndexNow</b> ile Bing &amp; Yandex’e
              dakikalar içinde index, <b>Search Console</b> ile sitemap submit + URL durumu,
              <b> AI ile iç bağlantı önerileri</b> ve <b>sosyal medya patlaması</b> tek panelde.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="ky-card p-2 flex flex-wrap gap-1 sticky top-0 z-10 backdrop-blur-md bg-white/80">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-3.5 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
              tab === t.id
                ? "bg-gradient-to-r from-[#0EA5E9] to-[#6366F1] text-white shadow-sm"
                : "text-[#64748B] hover:bg-[#F4F4F5]"
            }`}
          >
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      <div>
        {tab === "indexnow" && <IndexNowTab customers={customers} />}
        {tab === "gsc" && <GscTab customers={customers} />}
        {tab === "internal" && <InternalLinkTab customers={customers} />}
        {tab === "social" && <SocialBurstTab customers={customers} />}
        {tab === "history" && <HistoryTab />}
      </div>
    </div>
  );
}

/* ============================== TAB 1: IndexNow ============================== */
function IndexNowTab({ customers }) {
  const [host, setHost] = useState("");
  const [keyInfo, setKeyInfo] = useState(null);
  const [loadingKey, setLoadingKey] = useState(false);
  const [urlsText, setUrlsText] = useState("");
  const [sitemapUrl, setSitemapUrl] = useState("");
  const [extractedUrls, setExtractedUrls] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [customerId, setCustomerId] = useState("");

  const urlsParsed = useMemo(
    () => urlsText.split(/[\n\s,]+/).map((s) => s.trim()).filter(Boolean),
    [urlsText]
  );

  const fetchKey = async (hostOverride) => {
    const h = (hostOverride || host).trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/$/, "");
    if (!h) return toast.error("Önce site host (örn: lokmanestetik.com) yazın");
    setLoadingKey(true);
    try {
      const { data } = await apiClient.get(`/ai-tools/indexboost/indexnow/key?host=${encodeURIComponent(h)}`);
      setKeyInfo(data);
      if (data.fresh) toast.success("Yeni anahtar oluşturuldu — .txt dosyasını yüklemeyi unutmayın");
      return data;
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingKey(false);
    }
  };

  const extractSitemap = async () => {
    if (!sitemapUrl.trim()) return toast.error("Sitemap URL girin");
    setExtracting(true);
    try {
      const { data } = await apiClient.post("/ai-tools/indexboost/sitemap/extract", {
        sitemap_url: sitemapUrl.trim(),
        limit: 1000,
      });
      const urls = (data.urls || []).map((u) => u.url);
      setExtractedUrls(urls);
      // Auto-fill URLs field
      setUrlsText(urls.join("\n"));
      // Auto-detect host from first URL — saves the user a step.
      if (urls.length && !host.trim()) {
        try {
          const u = new URL(urls[0]);
          const detected = u.hostname.replace(/^www\./, "");
          setHost(detected);
          // Also fetch/create the IndexNow key proactively so the submit button is ready.
          fetchKey(detected);
        } catch (_) { /* noop */ }
      }
      toast.success(`${data.count} URL eklendi`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExtracting(false);
    }
  };

  const submit = async () => {
    const h = host.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/$/, "");
    if (!h) return toast.error("Site host gerekli — '1) Site & Anahtar' bölümünden domain girin.");
    if (!urlsParsed.length) return toast.error("En az 1 URL gerekli");
    // If key wasn't fetched yet, try to fetch it now (one-tap UX)
    let key = keyInfo;
    if (!key) {
      toast.message("IndexNow anahtarı alınıyor...");
      key = await fetchKey(h);
      if (!key) return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const { data } = await apiClient.post("/ai-tools/indexboost/indexnow/submit", {
        site_host: h,
        urls: urlsParsed,
        customer_id: customerId || null,
      });
      setResult(data);
      toast.success(`${data.url_count} URL gönderildi (${data.ok_engines}/${data.results.length} motor kabul etti)`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (s) => {
    navigator.clipboard.writeText(s);
    toast.success("Kopyalandı");
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      {/* LEFT: Setup */}
      <div className="lg:col-span-1 space-y-4">
        <div className="ky-card p-5 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#FEF3C7] flex items-center justify-center"><Key size={14} className="text-[#D97706]" /></div>
            <h3 className="font-semibold text-sm">1) Site &amp; Anahtar</h3>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#64748B] mb-1.5">Site Host (sadece domain)</label>
            <input
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="lokmanestetik.com"
              className="ky-input text-sm"
              data-testid="indexnow-host"
            />
          </div>
          <button onClick={() => fetchKey()} disabled={loadingKey} className="ky-btn ky-btn-outline w-full" data-testid="get-key-btn">
            {loadingKey ? <Loader2 size={14} className="animate-spin" /> : <Key size={14} />}
            Anahtarı Al / Oluştur
          </button>

          {keyInfo && (
            <div className="mt-2 bg-gradient-to-br from-[#F0FDFA] to-[#ECFEFF] border border-[#A7F3D0] rounded-lg p-3 text-xs space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[#047857] font-semibold flex items-center gap-1"><CheckCircle2 size={12} />Anahtar Hazır</span>
                <button onClick={() => copy(keyInfo.key)} className="text-[#0F766E] hover:underline text-[10px] font-medium flex items-center gap-1"><Copy size={10} />Kopyala</button>
              </div>
              <div className="font-mono text-[11px] break-all bg-white border rounded px-2 py-1.5">{keyInfo.key}</div>
              <div className="border-t border-[#A7F3D0] pt-2 mt-2 space-y-1.5">
                <div className="text-[10px] uppercase tracking-wider text-[#047857] font-semibold">Kurulum Adımları</div>
                <ol className="space-y-1 pl-3 list-decimal text-[#0F172A]">
                  <li>
                    Aşağıdaki <b>.txt</b> dosyasını oluşturun ve sitenizin kök dizinine yükleyin:
                  </li>
                  <li>
                    Dosya yolu: <code className="bg-white px-1.5 py-0.5 rounded border text-[10px]">https://{keyInfo.host}/{keyInfo.key}.txt</code>{" "}
                    <button onClick={() => copy(`https://${keyInfo.host}/${keyInfo.key}.txt`)} className="text-[#0F766E]"><Copy size={10} className="inline" /></button>
                  </li>
                  <li>
                    Dosya içeriği <b>tek satır</b>, sadece anahtarın kendisi: <code className="bg-white px-1.5 py-0.5 rounded border text-[10px]">{keyInfo.key}</code>{" "}
                    <button onClick={() => copy(keyInfo.key)} className="text-[#0F766E]"><Copy size={10} className="inline" /></button>
                  </li>
                  <li>Yükledikten sonra tarayıcıdan açıp düz metin görüntülendiğinden emin olun, sonra alttan toplu gönderim yapın.</li>
                </ol>
              </div>
            </div>
          )}
        </div>

        <div className="ky-card p-5 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#DBEAFE] flex items-center justify-center"><Globe size={14} className="text-[#1D4ED8]" /></div>
            <h3 className="font-semibold text-sm">2) URL Kaynağı</h3>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-[#64748B] mb-1">Sitemap.xml'den çek (opsiyonel)</label>
            <div className="flex gap-1.5">
              <input
                value={sitemapUrl}
                onChange={(e) => setSitemapUrl(e.target.value)}
                placeholder={`https://${host || "site"}.com/sitemap.xml`}
                className="ky-input text-sm flex-1"
                data-testid="sitemap-url"
              />
              <button onClick={extractSitemap} disabled={extracting} className="ky-btn ky-btn-outline shrink-0" data-testid="extract-sitemap">
                {extracting ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
              </button>
            </div>
            {extractedUrls.length > 0 && (
              <div className="text-[10px] text-[#16A34A] mt-1">{extractedUrls.length} URL aktarıldı</div>
            )}
          </div>

          <div>
            <label className="block text-[11px] font-medium text-[#64748B] mb-1">Müşteri (opsiyonel)</label>
            <CustomerSelect customers={customers} value={customerId || null} onChange={(v) => setCustomerId(v || "")} placeholder="— müşteri yok —" testid="indexnow-customer" />
          </div>
        </div>
      </div>

      {/* RIGHT: URL list + submit + results */}
      <div className="lg:col-span-2 space-y-4">
        <div className="ky-card p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#EDE9FE] flex items-center justify-center"><ListOrdered size={14} className="text-[#7C3AED]" /></div>
              3) Gönderilecek URL Listesi
            </h3>
            <span className="text-[11px] font-medium text-[#64748B]">{urlsParsed.length} URL</span>
          </div>
          <textarea
            value={urlsText}
            onChange={(e) => setUrlsText(e.target.value)}
            rows={10}
            placeholder={`https://${host || "site"}.com/sayfa-1\nhttps://${host || "site"}.com/sayfa-2`}
            className="ky-textarea text-sm font-mono"
            data-testid="indexnow-urls"
          />

          {/* Inline prerequisites banner — helps user see what's missing before submit */}
          {(!host.trim() || !keyInfo) && urlsParsed.length > 0 && (
            <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-lg p-3 text-xs space-y-1" data-testid="indexnow-prereq-banner">
              <div className="font-semibold text-[#92400E] flex items-center gap-1.5">
                <AlertTriangle size={12} /> Göndermeden önce eksikler:
              </div>
              <ul className="text-[#78350F] pl-4 list-disc space-y-0.5">
                {!host.trim() && <li>Site Host alanı boş (sol üstteki "1) Site &amp; Anahtar" kartından domain girin)</li>}
                {host.trim() && !keyInfo && <li>IndexNow anahtarı henüz oluşturulmadı. "Anahtarı Al / Oluştur" butonuna tıklayın <span className="text-[#94A3B8]">(göndere basınca otomatik denenecek)</span></li>}
              </ul>
            </div>
          )}

          <button
            onClick={submit}
            disabled={submitting || !urlsParsed.length}
            className="ky-btn ky-btn-accent w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="submit-indexnow"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
            {submitting ? "Gönderiliyor..." : `${urlsParsed.length} URL'i IndexNow ile Gönder`}
          </button>
        </div>

        {result && (
          <div className="ky-card p-5 space-y-3" data-testid="indexnow-result">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <CheckCircle2 size={16} className="text-[#16A34A]" /> Gönderim Sonucu
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {(result.results || []).map((r) => (
                <div
                  key={r.engine}
                  className={`p-3 rounded-lg border-2 ${
                    r.ok ? "border-[#A7F3D0] bg-[#F0FDFA]" : "border-[#FECACA] bg-[#FEF2F2]"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm capitalize">{r.engine}</span>
                    {r.ok ? <CheckCircle2 size={16} className="text-[#16A34A]" /> : <AlertTriangle size={16} className="text-[#DC2626]" />}
                  </div>
                  <div className="text-[10px] text-[#64748B] mt-1">HTTP {r.status_code}</div>
                  <div className="text-[11px] text-[#0F172A] mt-1 leading-snug">{r.detail}</div>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-[#64748B] border-t pt-2">
              <b>Not:</b> Bing/Yandex botları genelde 1-24 saat içinde URL'leri taramaya başlar. Google için "Search Console" sekmesindeki sitemap submit + URL inspection ile takip edebilirsiniz.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================== TAB 2: GSC ============================== */
function GscTab({ customers }) {
  const [gstatus, setGstatus] = useState(null);
  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [siteUrl, setSiteUrl] = useState("");
  const [sitemapUrl, setSitemapUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [inspectUrls, setInspectUrls] = useState("");
  const [inspectResult, setInspectResult] = useState(null);
  const [inspecting, setInspecting] = useState(false);

  useEffect(() => {
    apiClient.get("/integrations/google/status").then((r) => setGstatus(r.data)).catch(() => {});
  }, []);

  const loadSites = async () => {
    setLoadingSites(true);
    try {
      const { data } = await apiClient.get("/ai-tools/indexboost/gsc/sites");
      setSites(data.sites || []);
      if (!data.sites?.length) toast.info("Henüz onaylı Search Console mülk yok.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingSites(false);
    }
  };

  const submitSitemap = async () => {
    if (!siteUrl || !sitemapUrl) return toast.error("Mülk ve sitemap URL'i seçin");
    setSubmitting(true);
    try {
      await apiClient.post("/ai-tools/indexboost/gsc/sitemap-submit", {
        site_url: siteUrl,
        sitemap_url: sitemapUrl,
      });
      toast.success("Sitemap Google'a gönderildi");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const runInspect = async () => {
    const urls = inspectUrls.split(/[\n\s,]+/).map((s) => s.trim()).filter(Boolean);
    if (!siteUrl) return toast.error("Önce mülk seçin");
    if (!urls.length) return toast.error("İncelenecek URL girin");
    setInspecting(true);
    setInspectResult(null);
    try {
      const { data } = await apiClient.post("/ai-tools/indexboost/gsc/inspect", {
        site_url: siteUrl,
        urls,
      });
      setInspectResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setInspecting(false);
    }
  };

  if (gstatus && !gstatus.connected) {
    return (
      <div className="ky-card p-8 text-center space-y-3" data-testid="gsc-not-connected">
        <div className="w-12 h-12 mx-auto rounded-xl bg-[#FEF3C7] flex items-center justify-center">
          <AlertTriangle size={20} className="text-[#D97706]" />
        </div>
        <h3 className="font-semibold text-base">Google Hesabı Bağlı Değil</h3>
        <p className="text-sm text-[#64748B] max-w-md mx-auto">
          Search Console'a sitemap göndermek ve URL durumlarını sorgulamak için önce profil ayarlarınızdan Google hesabınızı bağlayın. (Yeni
          <b> Search Console </b> izni de eklendi — daha önce bağladıysanız, bağlantıyı kesip <i>yeniden onay</i> verin.)
        </p>
        <Link to="/yonetim" className="ky-btn ky-btn-accent inline-flex"><ExternalLink size={14} />Yönetim Paneline Git</Link>
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="ky-card p-5 space-y-3">
        <h3 className="font-semibold text-sm flex items-center gap-2"><ShieldCheck size={16} className="text-[#0EA5E9]" /> Mülklerinizi Yükleyin</h3>
        <button onClick={loadSites} disabled={loadingSites} className="ky-btn ky-btn-outline w-full" data-testid="load-gsc-sites">
          {loadingSites ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Search Console Mülklerini Yükle
        </button>
        {sites.length > 0 && (
          <div className="space-y-2 mt-2">
            <label className="block text-xs font-medium text-[#64748B]">Mülk seçin</label>
            <select value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} className="ky-input text-sm" data-testid="gsc-site-select">
              <option value="">— mülk seç —</option>
              {sites.map((s, i) => (
                <option key={i} value={s.siteUrl}>{s.siteUrl} ({s.permissionLevel})</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="ky-card p-5 space-y-3">
        <h3 className="font-semibold text-sm flex items-center gap-2"><FileDown size={16} className="text-[#7C3AED]" /> Sitemap Submit</h3>
        <input
          value={sitemapUrl}
          onChange={(e) => setSitemapUrl(e.target.value)}
          placeholder="https://siteniz.com/sitemap.xml"
          className="ky-input text-sm"
          data-testid="gsc-sitemap-url"
        />
        <button onClick={submitSitemap} disabled={submitting || !siteUrl} className="ky-btn ky-btn-accent w-full" data-testid="submit-gsc-sitemap">
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
          Sitemap'i Google'a Gönder
        </button>
      </div>

      <div className="ky-card p-5 space-y-3 lg:col-span-2">
        <h3 className="font-semibold text-sm flex items-center gap-2"><Search size={16} className="text-[#16A34A]" /> URL Durumu (Inspection — maks 25 URL)</h3>
        <textarea
          value={inspectUrls}
          onChange={(e) => setInspectUrls(e.target.value)}
          rows={6}
          placeholder="https://siteniz.com/sayfa-1\nhttps://siteniz.com/sayfa-2"
          className="ky-textarea text-sm font-mono"
          data-testid="gsc-inspect-urls"
        />
        <button onClick={runInspect} disabled={inspecting || !siteUrl} className="ky-btn ky-btn-outline w-full" data-testid="run-inspect">
          {inspecting ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          Index Durumunu Sorgula
        </button>

        {inspectResult && (
          <div className="space-y-2 mt-2">
            {(inspectResult.results || []).map((r, i) => (
              <div key={i} className="bg-[#FAFAFB] border border-[#ECECEF] rounded-lg p-3 text-xs">
                <div className="font-mono text-[11px] text-[#0F172A] break-all">{r.url}</div>
                {r.error ? (
                  <div className="text-[#DC2626] mt-1.5">{r.error}</div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                    <Cell label="Verdict" value={r.verdict} good={r.verdict === "PASS"} />
                    <Cell label="Indexing" value={r.indexing_state} />
                    <Cell label="Coverage" value={r.coverage_state} />
                    <Cell label="Robots" value={r.robots_state} />
                    <Cell label="Fetch" value={r.page_fetch_state} />
                    <Cell label="Son Tarama" value={r.last_crawl ? new Date(r.last_crawl).toLocaleDateString("tr-TR") : "-"} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Cell = ({ label, value, good }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{label}</div>
    <div className={`text-xs font-semibold ${good ? "text-[#16A34A]" : "text-[#0F172A]"}`}>{value || "-"}</div>
  </div>
);

/* ============================== TAB 3: Internal Links ============================== */
function InternalLinkTab({ customers }) {
  const [targetUrl, setTargetUrl] = useState("");
  const [targetTopic, setTargetTopic] = useState("");
  const [candText, setCandText] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const candidates = useMemo(() => {
    return candText
      .split(/\n+/)
      .map((line) => {
        const parts = line.split("|").map((s) => s.trim());
        if (!parts[0]) return null;
        return { url: parts[0], title: parts[1] || "", snippet: parts[2] || "" };
      })
      .filter(Boolean);
  }, [candText]);

  const run = async () => {
    if (!targetUrl || !targetTopic) return toast.error("Hedef URL ve konu gerekli");
    if (!candidates.length) return toast.error("En az 1 aday sayfa gerekli");
    setRunning(true);
    setResult(null);
    try {
      const { data } = await apiClient.post("/ai-tools/indexboost/internal-links", {
        target_url: targetUrl,
        target_topic: targetTopic,
        candidate_pages: candidates,
        customer_id: customerId || null,
      });
      setResult(data);
      toast.success(`${(data.suggestions || []).length} öneri üretildi`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-4">
      <div className="lg:col-span-2 space-y-4">
        <div className="ky-card p-5 space-y-3">
          <h3 className="font-semibold text-sm flex items-center gap-2"><Link2 size={16} className="text-[#0EA5E9]" /> Hedef Sayfa</h3>
          <input value={targetUrl} onChange={(e) => setTargetUrl(e.target.value)} placeholder="https://siteniz.com/yeni-sayfa" className="ky-input text-sm" data-testid="il-target-url" />
          <textarea value={targetTopic} onChange={(e) => setTargetTopic(e.target.value)} rows={3} placeholder="Konu, anahtar kelimeler veya sayfa özeti (örn: ortodonti tedavisi, şeffaf plak, kademe fiyatları)" className="ky-textarea text-sm" data-testid="il-topic" />
          <CustomerSelect customers={customers} value={customerId || null} onChange={(v) => setCustomerId(v || "")} placeholder="— müşteri yok —" testid="il-customer" />
        </div>

        <button onClick={run} disabled={running} className="ky-btn ky-btn-accent w-full justify-center" data-testid="il-run">
          {running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {running ? "AI Düşünüyor..." : "İç Bağlantı Önerileri Üret"}
        </button>
      </div>

      <div className="lg:col-span-3 space-y-4">
        <div className="ky-card p-5 space-y-2">
          <h3 className="font-semibold text-sm flex items-center gap-2"><ListOrdered size={16} className="text-[#7C3AED]" /> Aday Sayfa Havuzu</h3>
          <p className="text-[11px] text-[#64748B]">Her satıra bir sayfa, format: <code className="bg-[#F4F4F5] px-1 rounded">URL | Başlık | Kısa Özet</code></p>
          <textarea value={candText} onChange={(e) => setCandText(e.target.value)} rows={10} placeholder={"https://siteniz.com/blog/dis-implant-fiyatlari | Diş İmplant Fiyatları 2026 | İmplant fiyatları, taksit seçenekleri\nhttps://siteniz.com/hizmet/agiz-dis-sagligi | Ağız & Diş Sağlığı | Genel diş sağlığı hizmetleri…"} className="ky-textarea text-sm font-mono" data-testid="il-candidates" />
          <div className="text-[10px] text-[#94A3B8]">{candidates.length} aday algılandı</div>
        </div>

        {result && (
          <div className="space-y-2" data-testid="il-result">
            {(result.suggestions || []).length === 0 && (
              <div className="ky-card p-5 text-sm text-[#64748B] text-center">AI uygun bağlantı önermedi.</div>
            )}
            {(result.suggestions || []).map((s, i) => (
              <div key={i} className="ky-card p-4 space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[11px] text-[#0F172A] break-all">{s.source_url}</span>
                  <span className="ky-badge ky-badge-success">{s.alaka_skoru}/5</span>
                </div>
                <div className="text-sm font-semibold text-[#0F172A]">"{s.anchor_text}"</div>
                <div className="text-xs text-[#64748B] italic">{s.suggested_sentence}</div>
                {s.kisa_gerekce && <div className="text-[10px] text-[#94A3B8]">{s.kisa_gerekce}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================== TAB 4: Social Burst ============================== */
function SocialBurstTab({ customers }) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [channels, setChannels] = useState(["twitter", "linkedin", "instagram", "facebook"]);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const toggleChannel = (c) =>
    setChannels((arr) => (arr.includes(c) ? arr.filter((x) => x !== c) : [...arr, c]));

  const run = async () => {
    if (!url || !title) return toast.error("URL ve başlık gerekli");
    setRunning(true);
    setResult(null);
    try {
      const { data } = await apiClient.post("/ai-tools/indexboost/social-burst", {
        url,
        page_title: title,
        page_summary: summary,
        customer_id: customerId || null,
        channels,
      });
      setResult(data);
      toast.success(`${(data.posts || []).length} gönderi üretildi`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  const copy = (s) => { navigator.clipboard.writeText(s); toast.success("Kopyalandı"); };

  const CHANS = [
    { id: "twitter", label: "X (Twitter)", color: "#0F172A" },
    { id: "linkedin", label: "LinkedIn", color: "#0A66C2" },
    { id: "instagram", label: "Instagram", color: "#E1306C" },
    { id: "facebook", label: "Facebook", color: "#1877F2" },
    { id: "threads", label: "Threads", color: "#1F2937" },
  ];

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="space-y-4">
        <div className="ky-card p-5 space-y-3">
          <h3 className="font-semibold text-sm flex items-center gap-2"><Share2 size={16} className="text-[#E1306C]" /> Hedef Sayfa</h3>
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://siteniz.com/yeni-blog" className="ky-input text-sm" data-testid="sb-url" />
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Sayfa başlığı" className="ky-input text-sm" data-testid="sb-title" />
          <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={4} placeholder="Sayfa hakkında 2-3 cümlelik özet…" className="ky-textarea text-sm" data-testid="sb-summary" />
          <CustomerSelect customers={customers} value={customerId || null} onChange={(v) => setCustomerId(v || "")} placeholder="— müşteri yok —" testid="sb-customer" />
        </div>

        <div className="ky-card p-5 space-y-3">
          <h3 className="font-semibold text-sm">Kanallar</h3>
          <div className="flex flex-wrap gap-2">
            {CHANS.map((c) => (
              <button
                key={c.id}
                onClick={() => toggleChannel(c.id)}
                data-testid={`sb-chan-${c.id}`}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold border-2 transition-all ${
                  channels.includes(c.id) ? "border-[#0F172A] bg-[#0F172A] text-white" : "border-[#ECECEF] text-[#64748B]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <button onClick={run} disabled={running || !channels.length} className="ky-btn ky-btn-accent w-full justify-center" data-testid="sb-run">
          {running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {running ? "Yazılıyor..." : `${channels.length} kanal için draft üret`}
        </button>
      </div>

      <div className="space-y-3">
        {!result && <div className="ky-card p-8 text-center text-sm text-[#94A3B8]">Henüz draft yok. Sol taraftan başlatın.</div>}
        {result &&
          (result.posts || []).map((p, i) => (
            <div key={i} className="ky-card p-4 space-y-2" data-testid={`sb-post-${p.channel}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-xs uppercase tracking-wider text-[#0F172A]">{p.channel}</span>
                <button onClick={() => copy(p.text)} className="text-[#4F46E5] hover:underline text-[11px] font-medium flex items-center gap-1"><Copy size={12} />Kopyala</button>
              </div>
              <p className="text-sm text-[#0F172A] whitespace-pre-wrap leading-relaxed">{p.text}</p>
              {p.hashtags && p.hashtags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {p.hashtags.map((h, j) => (
                    <span key={j} className="text-[10px] bg-[#EEF2FF] text-[#4F46E5] px-1.5 py-0.5 rounded">{h}</span>
                  ))}
                </div>
              )}
              {p.character_count != null && (
                <div className="text-[10px] text-[#94A3B8]">{p.character_count} karakter</div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}

/* ============================== TAB 5: History ============================== */
function HistoryTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const q = filter ? `?kind=${filter}` : "";
      const { data } = await apiClient.get(`/ai-tools/indexboost/jobs${q}`);
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const remove = async (id) => {
    if (!window.confirm("Bu kayıt silinsin mi?")) return;
    try {
      await apiClient.delete(`/ai-tools/indexboost/jobs/${id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-3">
      <div className="ky-card p-3 flex flex-wrap gap-1.5">
        {[
          { id: "", label: "Tümü" },
          { id: "indexnow", label: "IndexNow" },
          { id: "internal_links", label: "İç Bağlantı" },
          { id: "social_burst", label: "Sosyal Patlama" },
        ].map((f) => (
          <button
            key={f.id || "all"}
            onClick={() => setFilter(f.id)}
            data-testid={`hist-filter-${f.id || "all"}`}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold ${
              filter === f.id ? "bg-[#0F172A] text-white" : "text-[#64748B] hover:bg-[#F4F4F5]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && <div className="ky-card p-8 text-center text-sm text-[#94A3B8]"><Loader2 size={16} className="animate-spin inline mr-2" />Yükleniyor…</div>}
      {!loading && !items.length && <div className="ky-card p-8 text-center text-sm text-[#94A3B8]">Henüz kayıt yok.</div>}
      {!loading && items.map((it) => (
        <div key={it.id} className="ky-card p-4 flex items-start justify-between gap-3" data-testid={`hist-item-${it.id}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="ky-badge ky-badge-neutral text-[10px]">{it.kind}</span>
              <span className="text-[11px] text-[#64748B]">{new Date(it.created_at).toLocaleString("tr-TR")}</span>
            </div>
            <div className="text-sm font-semibold text-[#0F172A] mt-1 truncate">
              {it.kind === "indexnow" && `${it.url_count} URL → ${it.site_host}`}
              {it.kind === "internal_links" && `${it.target_topic} → ${(it.suggestions || []).length} öneri`}
              {it.kind === "social_burst" && `${it.page_title} → ${(it.posts || []).length} post`}
            </div>
            <div className="text-[11px] text-[#64748B] truncate">
              {it.kind === "indexnow" && `${it.ok_engines}/${(it.results || []).length} motor kabul etti`}
              {it.kind === "internal_links" && it.target_url}
              {it.kind === "social_burst" && it.url}
            </div>
          </div>
          <button onClick={() => remove(it.id)} className="text-[#DC2626] hover:bg-[#FEF2F2] p-1.5 rounded" title="Sil"><Trash2 size={14} /></button>
        </div>
      ))}
    </div>
  );
}
